import React from 'react';

import { useSelector } from 'react-redux';

import { isEqual } from 'lodash-es';
import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import Wrapper, {
	Content,
	Footer,
	FooterSection,
	Header,
} from '@asteria/component-core/wrapper';

import { Wrapper as FormWrapper } from '@asteria/component-form';
import Checkbox from '@asteria/component-form/checkbox';
import { ScenarioSection } from '@asteria/component-settings';
import { FeatureFlag } from '@asteria/component-tools/featureflag';

import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { TranslationService } from '@asteria/language';
import { useTranslate } from '@asteria/language';

import SelectScenarioModal from '../scenario/select';

import ResetSection from './reset';

import './styles.scss';

const ForecasterSettingsContent = React.memo((props) => {
	const { onClose, onAction, onSubmit, loading, form } = props;
	const [showModal, setShowModal] = React.useState(false);

	const scenario = useSelector(
		(store) => ScenarioStore.selectors.sourceScenarioData(store),
		isEqual,
	);

	const onSelect = React.useCallback(
		(event, data) => {
			if (event === 'scenario:selected') {
				setShowModal(false);
				onClose();
			}

			onSubmit?.(event, data);
		},
		[onSubmit, onClose],
	);

	const { t } = useTranslate();

	return (
		<Wrapper>
			<Header onClose={onClose} verticalAlign="center">
				{t('forecaster.modal.settings.title')}
			</Header>
			<Content>
				<SelectScenarioModal
					open={showModal}
					onSubmit={onSelect}
					selectedScenario={scenario}
				/>
				<FormWrapper>
					<Content>
						<FeatureFlag feature="forecaster-scenarios">
							<ScenarioSection onAction={onAction} prefix="" />
						</FeatureFlag>

						<FeatureFlag feature="forecaster-settings-reset">
							<ResetSection onAction={onAction} form={form} />
						</FeatureFlag>

						<Button
							variant="tertiary"
							label={TranslationService.get(
								'forecaster.modal.settings.button.select.scenario',
							)}
							onClick={() => setShowModal(true)}
						/>

						<FeatureFlag feature="forecaster-show-history-backdrop">
							<Checkbox
								name="flags.forecasterShowHistory"
								label={t(
									'forecaster.graph.option.show.history',
								)}
							/>
						</FeatureFlag>
					</Content>
				</FormWrapper>
			</Content>
			<Footer>
				<FooterSection position="first">
					<Button
						variant="tertiary"
						label={t([
							'button.abort',
							'forecaster.modal.settings.button.abort',
						])}
						onClick={onClose}
						analyticsKey="forecaster.settings.cancel"
					/>
				</FooterSection>
				<FooterSection position="last">
					<Button
						variant="primary"
						label={t([
							'button.save',
							'forecaster.modal.settings.button.save',
						])}
						type="submit"
						analyticsKey="forecaster.settings.save"
						disabled={loading}
						loading={loading}
					/>
				</FooterSection>
			</Footer>
		</Wrapper>
	);
});

ForecasterSettingsContent.displayName = 'ForecasterSettingsContent';

ForecasterSettingsContent.propTypes = {
	onClose: PropTypes.func,
	onAction: PropTypes.func,
	loading: PropTypes.bool,

	form: PropTypes.object,
};

export default ForecasterSettingsContent;
